import * as z from "zod";
import { validateWeight, WEIGHT_ISSUE_KEYS } from "@/utils/weight";

// Basic schema for a single amount value
const singleAmountSchema = z.coerce.number().min(0, { message: "validation:amountMustBePositive" });

// For handling a single amount (used in simple cases)
export const FinalAmountValidationSchema = z.object({
  amount: singleAmountSchema,
});

// A weight typed in kg or tonnes. The amount stays as typed (a string while the field is empty or half-typed), and the
// message of the first thing wrong with it is a translation key, put on the weight itself.
const weightInputSchema = z
  .object({
    amount: z.union([z.number(), z.string()]),
    unit: z.enum(["KG", "TONNES"]),
  })
  .superRefine((weight, ctx) => {
    const issue = validateWeight(weight);
    if (issue) ctx.addIssue({ code: z.ZodIssueCode.custom, message: WEIGHT_ISSUE_KEYS[issue] });
  });

// For a kg or tonnes order. The backend decides whether the weight is plausible, so there is no limit here.
export const WeightFinalAmountValidationSchema = z.object({
  weight: weightInputSchema,
});

// For the multiple final amount task, with dynamic keys based on order IDs: a weight for an order counted in kg or
// tonnes and a plain amount for the others.
export const MultipleMixedFinalAmountValidationSchema = z.record(
  z.string(),
  z.union([weightInputSchema, singleAmountSchema])
);

// Type for a single amount form
export type FinalAmountFormInput = z.infer<typeof FinalAmountValidationSchema>;

// Type for a single weight form
export type WeightFinalAmountFormInput = z.infer<typeof WeightFinalAmountValidationSchema>;

// Type for the multiple final amount form, which mixes weights and plain amounts
export type MultipleMixedFinalAmountFormInput = z.infer<typeof MultipleMixedFinalAmountValidationSchema>;
